/** `rebuild` — the whole model turned into meshes, and the BOM that describes them. */

import type { Group } from "three";
import { basePFor, nodeY } from "../../data/haki.ts";
import { kXZ } from "../../model/geometry.ts";
import { pruneUnsupported } from "../../model/operations.ts";
import { jackExtension, type PlankFamily } from "../../model/solver.ts";
import type { ScaffoldModel } from "../../model/types.ts";
import { BomCollector, type BomRow } from "../bom.ts";
import type { Terrain } from "../terrain.ts";
import { renderBeams, renderDiagonals } from "./beams.ts";
import { renderBrackets } from "./brackets.ts";
import type { RenderContext, SolvedLeg } from "./context.ts";
import { renderBoards, renderDecks } from "./decks.ts";
import { renderLadders } from "./ladders.ts";
import { renderRails, renderToeBoards } from "./rails.ts";
import { renderStairs } from "./stairs.ts";
import { renderStandards } from "./standards.ts";

export type JackSolution = Readonly<{ ext: number; raised: boolean }>;

export type RenderResult = Readonly<{
  bomRows: BomRow[];
  totalKg: number;
  jacks: JackSolution[];
  /** True when a diagonal had to be booked as loose tube + couplers. */
  anyNonModuleDiagonal: boolean;
  pickElems: RenderContext["pickElems"];
  pickPlanks: RenderContext["pickPlanks"];
  pickEnds: RenderContext["pickEnds"];
}>;

export type RebuildOptions = Readonly<{
  model: ScaffoldModel;
  terrain: Terrain;
  root: Group;
  selectedLegKey: string | null;
  plankFam: PlankFamily;
}>;

/**
 * Clear `root` and re-render the whole model into it.
 *
 * Ground heights and base levels are re-solved first — a leg keeps its own
 * surface by casting from just above where it last stood, and a leg on a
 * bracket takes its base from the bracket — and parts whose supports have gone
 * are pruned. That prune is the only mutation rendering performs on the model.
 */
export function rebuild(options: RebuildOptions): RenderResult {
  const { model, terrain, root, selectedLegKey, plankFam } = options;

  root.clear();
  pruneUnsupported(model);

  const legs: SolvedLeg[] = [];
  for (const leg of model.legs.values()) {
    if (leg.mountP !== undefined) {
      leg.gy = nodeY(leg.mountP);
      leg.baseP = leg.mountP;
    } else {
      leg.gy = terrain.resolveGy(leg.x, leg.z, leg.gy);
      leg.baseP = basePFor(leg.gy);
    }
    legs.push({ ...leg, key: kXZ(leg.x, leg.z), gy: leg.gy, baseP: leg.baseP });
  }

  const ctx: RenderContext = {
    model,
    terrain,
    bom: new BomCollector(),
    root,
    pickElems: [],
    pickPlanks: [],
    pickEnds: [],
    legs,
    selectedLegKey,
    plankFam,
  };

  renderStandards(ctx);
  renderBrackets(ctx);
  renderBeams(ctx);
  const anyNonModuleDiagonal = renderDiagonals(ctx);
  renderDecks(ctx);
  renderBoards(ctx);
  renderStairs(ctx);
  renderLadders(ctx);
  renderRails(ctx);
  renderToeBoards(ctx);

  const { rows, totalKg } = ctx.bom.result();

  return {
    bomRows: rows,
    totalKg,
    jacks: legs
      .filter((leg) => leg.mountP === undefined)
      .map((leg) => ({ ext: jackExtension(leg), raised: leg.baseP > 0 })),
    anyNonModuleDiagonal,
    pickElems: ctx.pickElems,
    pickPlanks: ctx.pickPlanks,
    pickEnds: ctx.pickEnds,
  };
}
